import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API, api, coverUrl, streamUrl, transcoded } from "@/lib/api";
import { isProtected, needsTranscode } from "@/lib/formats";
import { loadPlayer, savePlayer } from "@/lib/offline";
import { fadeCurve, isContinuation, loadPlayback, savePlayback, DEFAULT_PLAYBACK } from "@/lib/playback";
import { applyEq, attachDecks, loadEq, saveEq, DEFAULT_EQ } from "@/lib/equalizer";
import { onPrefs, pushPref, pushPlayer, pushPlayerNow, playerPushedAgo } from "@/lib/prefsSync";

export const stationKey = (s) => (s.id ? `s-${s.id}` : `d-${s.rb_uuid}`);

const PlayerCtx = createContext(null);
const PRELOAD_AHEAD = 10;

export function PlayerProvider({ children }) {
  // two-deck player: `audioRef` is the active deck, the other preloads the next song (gapless / crossfade)
  const decks = useRef(null);
  if (!decks.current) decks.current = [new Audio(), new Audio()].map((a) => { a.preload = "auto"; return a; });
  const audioRef = useRef(decks.current[0]);
  const spareOf = () => decks.current[audioRef.current === decks.current[0] ? 1 : 0];
  const fade = useRef(null);
  const planned = useRef({ index: -1, n: -1 });
  const saved = useRef(loadPlayer());
  const pendingSeek = useRef(saved.current?.time || 0);
  const [queue, setQueue] = useState(() => (saved.current?.queue?.length && !saved.current.queue.some((t) => t.live) ? saved.current.queue : []));
  const [index, setIndex] = useState(() => (saved.current?.queue?.length && !saved.current.queue.some((t) => t.live) ? saved.current.index : -1));
  const [token, setToken] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(() => Number(localStorage.getItem("vol") ?? 0.8));
  const [muted, setMuted] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off");
  const [radio, setRadio] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playback, setPlaybackState] = useState(loadPlayback);
  const [eq, setEqState] = useState(loadEq);
  const [transcodeTick, setTranscodeTick] = useState(0);
  const [rate, setRateState] = useState(() => Number(localStorage.getItem("rh.rate") || 1));
  const [sleep, setSleepState] = useState(null); // { until: epoch ms } | { chapter: true }
  const startAt = useRef(0);
  const sleepTimer = useRef(null);
  const sleepRef = useRef(null);
  useEffect(() => { sleepRef.current = sleep; }, [sleep]);
  const mounted = useRef(false);
  useEffect(() => { saveEq(eq); applyEq(eq); if (mounted.current) pushPref("eq", eq); }, [eq]);
  useEffect(() => {
    onPrefs("eq", { apply: (v) => setEqState({ ...DEFAULT_EQ, ...v, gains: v?.gains?.length === 10 ? v.gains : DEFAULT_EQ.gains }), read: loadEq });
    onPrefs("playback", { apply: (v) => setPlaybackState({ ...DEFAULT_PLAYBACK, ...v }), read: loadPlayback });
    onPrefs("rate", { apply: (v) => setRate(v), read: () => rateRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const armEq = useCallback(() => { if (attachDecks(decks.current)) applyEq(eq); }, [eq]);
  const [crossfading, setCrossfading] = useState(false);
  const refilling = useRef(false);
  const state = useRef({});
  state.current = { queue, index, shuffle, repeat, volume, muted, playback };
  const current = queue[index];

  const playAt = useCallback((i) => { setIndex(i); setToken((t) => t + 1); }, []);

  const peekNext = useCallback((auto) => {
    const { queue: q, index: i, shuffle: sh, repeat: rp } = state.current;
    if (!q.length) return -1;
    if (sh && q.length > 1) {
      if (planned.current.index !== i) planned.current = { index: i, n: (i + 1 + Math.floor(Math.random() * (q.length - 1))) % q.length };
      return planned.current.n;
    }
    const n = i + 1;
    if (n >= q.length) return rp === "all" || !auto ? 0 : -1;
    return n;
  }, []);

  const next = useCallback((auto = false) => {
    const n = peekNext(auto);
    if (n < 0) { if (state.current.queue.length) audioRef.current.pause(); return; }
    playAt(n);
  }, [playAt, peekNext]);

  const finishFade = useCallback(() => {
    const f = fade.current;
    if (!f) return;
    clearInterval(f.timer);
    f.old.pause();
    f.old.volume = state.current.volume;
    f.old.dataset.trackId = "";
    audioRef.current.volume = state.current.volume;
    fade.current = null;
    setCrossfading(false);
  }, []);

  // make `deck` the active one and point the UI at queue[n]
  const handoff = useCallback((n, deck) => {
    const nt = state.current.queue[n];
    audioRef.current = deck;
    deck.muted = state.current.muted;
    deck.playbackRate = nt.is_audiobook ? rateRef.current : 1;
    pendingSeek.current = 0;
    setIndex(n);
    setTime(deck.currentTime || 0);
    setDuration(nt.duration || deck.duration || 0);
    if (!nt.shared_pid && !nt.lib) api.post(`/tracks/${nt.id}/play`).catch(() => {});
  }, []);

  const startFade = useCallback((n, seconds) => {
    const old = audioRef.current, deck = spareOf();
    deck.volume = 0;
    deck.muted = state.current.muted;
    deck.play().catch(() => {});
    handoff(n, deck);
    const t0 = performance.now();
    const timer = setInterval(() => {
      const p = Math.min(1, (performance.now() - t0) / (seconds * 1000));
      const { out, in: inn } = fadeCurve(p);
      old.volume = state.current.volume * out;
      deck.volume = state.current.volume * inn;
      if (p >= 1) finishFade();
    }, 40);
    fade.current = { timer, old };
    setCrossfading(true);
  }, [handoff, finishFade]);

  const tick = useCallback((a) => {
    setTime(a.currentTime);
    const { queue: q, index: i, repeat: rp, shuffle: sh, playback: pb } = state.current;
    const t = q[i];
    if (!t || t.live || rp === "one" || fade.current || !a.duration || a.paused) return;
    const n = peekNext(true);
    const nt = q[n];
    if (n < 0 || !nt || nt.live) return;
    const cf = t.is_audiobook || (pb.gaplessAlbums && isContinuation(t, nt, n, i, sh)) ? 0 : pb.crossfade;
    const remaining = a.duration - a.currentTime;
    if (remaining > cf + PRELOAD_AHEAD) return;
    const sp = spareOf();
    if (sp.dataset.trackId !== nt.id) { if (needsTranscode(nt)) transcoded.add(nt.id); sp.src = streamUrl(nt.id); sp.dataset.trackId = nt.id; sp.load(); }
    if (cf > 0 && remaining <= cf && remaining > 0.25 && sp.readyState >= 2) startFade(n, Math.min(cf, remaining));
  }, [peekNext, startFade]);

  const nextRef = useRef(next);
  nextRef.current = next;
  const tickRef = useRef(tick);
  tickRef.current = tick;
  const handoffRef = useRef(handoff);
  handoffRef.current = handoff;
  const peekRef = useRef(peekNext);
  peekRef.current = peekNext;

  useEffect(() => {
    const offs = decks.current.map((a) => {
      const active = () => a === audioRef.current;
      const onTime = () => active() && tickRef.current(a);
      const onDur = () => active() && setDuration(a.duration || 0);
      const onPlay = () => active() && setPlaying(true);
      const onPause = () => active() && setPlaying(false);
      const onEnd = () => {
        if (!active()) return;
        if (sleepRef.current?.chapter) { sleepRef.current = null; setSleepState(null); toast.message("Sleep timer: end of chapter"); return; }
        if (state.current.repeat === "one") { a.currentTime = 0; a.play(); return; }
        const n = peekRef.current(true);
        const cur = state.current.queue[state.current.index];
        if (cur?.is_audiobook && n < 0) window.dispatchEvent(new CustomEvent("audiobook-finished", { detail: { album_id: cur.album_id, track_id: cur.id, duration: a.duration || cur.duration || 0 } }));
        const nt = state.current.queue[n];
        const sp = decks.current[a === decks.current[0] ? 1 : 0];
        if (n >= 0 && nt && !nt.live && sp.dataset.trackId === nt.id && sp.readyState >= 2) {
          sp.volume = state.current.volume;
          handoffRef.current(n, sp);
          sp.play().catch(() => {});
          a.dataset.trackId = "";
        } else nextRef.current(true);
      };
      const onError = () => {
        if (!active()) return;
        const t = state.current.queue[state.current.index];
        if (t?.live && a.getAttribute("src")) { toast.error(`Couldn't play ${t.title}`, { description: "The station may be offline or use an unsupported format." }); return; }
        const code = a.error?.code;
        if (t && !t.live && !t.shared_pid && a.getAttribute("src") && (code === 3 || code === 4) && !transcoded.has(t.id)) {
          transcoded.add(t.id);
          setTranscodeTick((x) => x + 1);
          const at = a.currentTime || 0;
          a.src = streamUrl(t.id);
          if (at) a.addEventListener("loadedmetadata", () => { a.currentTime = at; }, { once: true });
          a.play().catch(() => {});
          toast.message(`Converting ${t.title} for this browser…`, { description: "This format isn't supported natively; playing a converted copy." });
        } else if (t && !t.live && (code === 3 || code === 4)) {
          toast.error(`Couldn't play ${t.title}`, { description: "This file could not be decoded or converted." });
        }
      };
      const pairs = [["timeupdate", onTime], ["durationchange", onDur], ["play", onPlay], ["pause", onPause], ["ended", onEnd], ["error", onError]];
      pairs.forEach(([k, fn]) => a.addEventListener(k, fn));
      return () => pairs.forEach(([k, fn]) => a.removeEventListener(k, fn));
    });
    return () => offs.forEach((off) => off());
  }, []);

  useEffect(() => {
    if (!fade.current) audioRef.current.volume = volume;
    decks.current.forEach((a) => { a.muted = muted; });
    localStorage.setItem("vol", volume);
  }, [volume, muted]);

  useEffect(() => { savePlayback(playback); if (mounted.current) pushPref("playback", playback); }, [playback]);
  useEffect(() => { planned.current.index = -1; }, [queue]);

  useEffect(() => {
    const t = state.current.queue[state.current.index];
    if (!token || !t) return;
    finishFade();
    const a = audioRef.current;
    a.dataset.trackId = t.live ? "" : t.id;
    if (isProtected(t)) { a.removeAttribute("src"); setTime(0); toast.error(`${t.title} is copy-protected`, { description: "Audible / iTunes DRM files can't be played here. Skip to the next chapter or remove the protection with the vendor's own tools." }); return; }
    if (!t.live && needsTranscode(t)) transcoded.add(t.id);
    a.src = t.live ? `${API}/radio/play/${t.key}/stream` : streamUrl(t.id);
    pendingSeek.current = 0;
    const at = startAt.current; startAt.current = 0;
    if (at > 0) a.addEventListener("loadedmetadata", () => { a.currentTime = at; setTime(at); }, { once: true });
    setTime(at > 0 ? at : 0);
    setDuration(t.duration || 0);
    a.playbackRate = t.is_audiobook ? rateRef.current : 1;
    armEq();
    a.play().catch(() => {});
    if (!t.live && !t.shared_pid && !t.lib) api.post(`/tracks/${t.id}/play`).catch(() => {});
  }, [token, finishFade]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (queue.some((t) => t.live)) return;
    const snap = { queue: queue.slice(0, 500), index, time: audioRef.current.currentTime || pendingSeek.current || 0, at: Date.now() };
    savePlayer(snap);
    if (mounted.current) pushPlayer(snap);
  }, [queue, index]);
  useEffect(() => {
    if (!playing) return undefined;
    const iv = setInterval(() => {
      const { queue: q, index: i } = state.current;
      if (q.some((t) => t.live)) return;
      const snap = { queue: q.slice(0, 500), index: i, time: audioRef.current.currentTime || 0, at: Date.now() };
      savePlayer(snap);
      if (playerPushedAgo() > 15000) pushPlayer(snap, { delay: 0 });
    }, 5000);
    return () => clearInterval(iv);
  }, [playing]);
  useEffect(() => {
    // Pause: save the exact spot right away so another device can pick up from here.
    if (playing || !mounted.current) return;
    const { queue: q, index: i } = state.current;
    if (i < 0 || q.some((t) => t.live)) return;
    pushPlayer({ queue: q.slice(0, 500), index: i, time: audioRef.current.currentTime || 0 }, { delay: 300 });
  }, [playing]);
  useEffect(() => {
    const flushNow = (e) => {
      const { queue: q, index: i } = state.current;
      if ((e.type === "visibilitychange" && document.visibilityState === "visible") || q.some((t) => t.live)) return;
      pushPlayerNow({ queue: q.slice(0, 500), index: i, time: audioRef.current.currentTime || pendingSeek.current || 0 });
    };
    document.addEventListener("visibilitychange", flushNow);
    window.addEventListener("pagehide", flushNow);
    return () => { document.removeEventListener("visibilitychange", flushNow); window.removeEventListener("pagehide", flushNow); };
  }, []);
  useEffect(() => {
    if (!current || token) return;
    setTime(pendingSeek.current);
    setDuration(current.duration || 0);
  }, [current, token]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    if (!current) { ms.metadata = null; return; }
    ms.metadata = new window.MediaMetadata({
      title: current.live ? (nowPlaying || current.title) : current.title, artist: current.artist || (current.live ? "Live radio" : ""), album: current.album || "",
      artwork: current.album_id ? [{ src: coverUrl(current.album_id), sizes: "512x512", type: "image/jpeg" }] : current.favicon ? [{ src: current.favicon }] : [],
    });
    ms.playbackState = playing ? "playing" : "paused";
  }, [current, playing, nowPlaying]);

  const playTracks = useCallback((tracks, start = 0, at = 0) => {
    if (!tracks?.length) return;
    setRadio(null);
    startAt.current = at || 0;
    setQueue(tracks);
    playAt(start);
  }, [playAt]);

  /* Load a queue paused at a position (resume from another device) — nothing plays until the user presses play. */
  const loadTracks = useCallback((tracks, start = 0, at = 0) => {
    if (!tracks?.length) return;
    finishFade();
    decks.current.forEach((a) => { a.pause(); a.removeAttribute("src"); a.dataset.trackId = ""; });
    setRadio(null);
    pendingSeek.current = at || 0;
    setQueue(tracks);
    setIndex(Math.min(start, tracks.length - 1));
    setTime(at || 0);
    setDuration(tracks[start]?.duration || 0);
  }, [finishFade]);

  const playStation = useCallback((station) => {
    const key = stationKey(station);
    setRadio(null);
    setNowPlaying(null);
    setQueue([{ id: `radio:${key}`, live: true, key, title: station.name, favicon: station.favicon, station }]);
    playAt(0);
  }, [playAt]);

  const startRadio = useCallback(async (seed_type, seed) => {
    try {
      const { data } = await api.post("/radio/library", { seed_type, seed });
      if (!data.tracks.length) { toast.error("Not enough songs in your library for this radio"); return; }
      setQueue(data.tracks);
      playAt(0);
      setRadio({ seed_type, seed, label: data.label });
      toast.success(`${data.label} started`, { description: data.similar?.length ? `With ${data.similar.slice(0, 3).join(", ")} and more` : undefined });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start radio"); }
  }, [playAt]);

  useEffect(() => {
    if (!radio || refilling.current || queue.length - index > 3) return;
    refilling.current = true;
    api.post("/radio/library", { ...radio, seed_type: radio.seed_type, seed: radio.seed, exclude: queue.slice(-500).map((t) => t.id) })
      .then(({ data }) => setQueue((q) => [...q, ...data.tracks.filter((t) => !q.some((x) => x.id === t.id))]))
      .catch(() => {})
      .finally(() => { refilling.current = false; });
  }, [radio, index, queue]);

  useEffect(() => {
    if (!current?.live) { setNowPlaying(null); return; }
    let alive = true;
    const load = () => api.get(`/radio/play/${current.key}/now`).then((r) => alive && setNowPlaying(r.data.title)).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [current?.live, current?.key]);

  const addToQueue = useCallback((tracks) => {
    setQueue((q) => {
      if (!q.length) { setTimeout(() => playAt(0), 0); }
      return [...q, ...tracks];
    });
  }, [playAt]);

  const playNext = useCallback((tracks) => {
    setQueue((q) => {
      if (!q.length) { setTimeout(() => playAt(0), 0); return [...tracks]; }
      const i = state.current.index;
      return [...q.slice(0, i + 1), ...tracks, ...q.slice(i + 1)];
    });
  }, [playAt]);

  const removeAt = useCallback((i) => {
    const { index: cur } = state.current;
    setQueue((q) => q.filter((_, k) => k !== i));
    if (i < cur) setIndex(cur - 1);
    else if (i === cur) { audioRef.current.pause(); setToken((t) => t + 1); }
  }, []);

  const moveInQueue = useCallback((from, to) => {
    const cur = state.current.index;
    if (from === to || from <= cur || to <= cur) return;
    setQueue((q) => { const n = [...q]; const [it] = n.splice(from, 1); n.splice(to, 0, it); return n; });
  }, []);

  const clear = useCallback(() => {
    finishFade();
    decks.current.forEach((a) => { a.pause(); a.removeAttribute("src"); a.dataset.trackId = ""; });
    setQueue([]);
    setIndex(-1);
    setRadio(null);
  }, [finishFade]);

  const toggle = useCallback(() => {
    finishFade();
    const a = audioRef.current;
    if (!state.current.queue.length) return;
    const t = state.current.queue[state.current.index];
    if (a.paused) {
      if (t?.live) a.src = `${API}/radio/play/${t.key}/stream`;
      else if (!a.getAttribute("src") && t) {
        if (isProtected(t)) { toast.error(`${t.title} is copy-protected`, { description: "Audible / iTunes DRM files can't be played here." }); return; }
        const at = pendingSeek.current;
        if (needsTranscode(t)) transcoded.add(t.id);
        a.src = streamUrl(t.id);
        a.dataset.trackId = t.id;
        if (at) a.addEventListener("loadedmetadata", () => { a.currentTime = at; }, { once: true });
      }
      armEq();
      a.play().catch(() => {});
    } else a.pause();
  }, [finishFade, armEq]);

  const prev = useCallback(() => {
    const a = audioRef.current;
    if (a.currentTime > 3 || state.current.index <= 0) { a.currentTime = 0; return; }
    playAt(state.current.index - 1);
  }, [playAt]);

  const seek = useCallback((t) => {
    audioRef.current.currentTime = t; setTime(t);
    const { queue: q, index: i } = state.current;
    if (i >= 0 && !q.some((x) => x.live)) pushPlayer({ queue: q.slice(0, 500), index: i, time: t }, { delay: 1500 });
  }, []);
  const skipBy = useCallback((sec) => {
    const a = audioRef.current;
    const nt = Math.max(0, Math.min((a.duration || state.current.queue[state.current.index]?.duration || Infinity) - 0.5, (a.currentTime || 0) + sec));
    a.currentTime = nt; setTime(nt);
  }, []);
  const rateRef = useRef(rate);
  const setRate = useCallback((r) => {
    const v = Math.min(3, Math.max(0.5, Number(r) || 1));
    rateRef.current = v; setRateState(v); localStorage.setItem("rh.rate", String(v));
    pushPref("rate", v);
    if (state.current.queue[state.current.index]?.is_audiobook) decks.current.forEach((d) => { d.playbackRate = v; });
  }, []);
  const setSleep = useCallback((opt) => {
    clearTimeout(sleepTimer.current);
    if (!opt) { setSleepState(null); return; }
    if (opt.minutes) {
      const until = Date.now() + opt.minutes * 60000;
      setSleepState({ until });
      sleepTimer.current = setTimeout(() => { audioRef.current.pause(); setSleepState(null); toast.message("Sleep timer: playback paused"); }, opt.minutes * 60000);
    } else if (opt.chapter) setSleepState({ chapter: true });
  }, []);
  const setVolume = useCallback((v) => { setVolumeState(v); setMuted(false); }, []);
  const setPlayback = useCallback((patch) => setPlaybackState((p) => ({ ...p, ...patch })), []);
  const setEq = useCallback((patch) => setEqState((e) => ({ ...e, ...(typeof patch === "function" ? patch(e) : patch) })), []);
  const updateTrack = useCallback((id, patch) => {
    setQueue((q) => q.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    const handlers = { play: toggle, pause: toggle, previoustrack: prev, nexttrack: () => next(false),
      seekto: (d) => { if (d.seekTime != null) seek(d.seekTime); } };
    Object.entries(handlers).forEach(([k, fn]) => { try { ms.setActionHandler(k, fn); } catch { /* unsupported action */ } });
    return () => Object.keys(handlers).forEach((k) => { try { ms.setActionHandler(k, null); } catch { /* noop */ } });
  }, [toggle, prev, next, seek]);
  useEffect(() => {
    // Thumbs-down on the playing song: move on right away.
    const onDislike = (e) => {
      const cur = state.current.queue[state.current.index];
      if (!cur || cur.id !== e.detail || cur.live) return;
      if (peekNext(false) >= 0) { next(false); toast("Skipped — thumbs-down songs stay out of your mixes"); }
      else { audioRef.current.pause(); toast("Stopped — that was the last song in the queue"); }
    };
    window.addEventListener("track-disliked", onDislike);
    return () => window.removeEventListener("track-disliked", onDislike);
  }, [next, peekNext]);
  const cycleRepeat = useCallback(() => setRepeat((r) => (r === "off" ? "all" : r === "all" ? "one" : "off")), []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Warm the server-side disk cache for the current song and the one after it, so FileJump tracks start instantly.
  const nextId = current && !current.live && repeat !== "one" ? queue[peekNext(false)]?.id ?? null : null;
  useEffect(() => {
    if (!playing) return undefined;
    const ids = [current?.id, nextId].filter((id, i, arr) => id && arr.indexOf(id) === i);
    if (!ids.length) return undefined;
    const t = setTimeout(() => { api.post("/cache/warm", { track_ids: ids }).catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [current?.id, nextId, playing]);

  const pause = useCallback(() => { if (state.current.queue.length && !audioRef.current.paused) toggle(); }, [toggle]);
  const value = {
    queue, index, current, playing, time, duration, volume, muted, shuffle, repeat, pause,
    transcoding: !!current && !current.live && transcoded.has(current.id), // transcodeTick state forces a re-render when the fallback kicks in
    transcodeTick,
    nextTrack: current && !current.live && repeat !== "one" ? queue[peekNext(false)] ?? null : null,
    upcoming: (() => {
      if (!current || current.live || repeat === "one") return [];
      if (shuffle) { const n = peekNext(false); return n >= 0 ? [{ track: queue[n], index: n }] : []; }
      const out = [];
      for (let k = 1; k <= 3; k++) {
        const i = index + k;
        if (i < queue.length) out.push({ track: queue[i], index: i });
        else if (repeat === "all" && queue.length > 1 && i - queue.length < index) out.push({ track: queue[i - queue.length], index: i - queue.length });
      }
      return out;
    })(),
    prevTrack: current && !current.live && index > 0 ? queue[index - 1] : null,
    playTracks, loadTracks, addToQueue, playNext, removeAt, moveInQueue, clear, toggle, next: () => next(false), prev, seek, playAt,
    rate, setRate, skipBy, sleep, setSleep,
    setVolume, setMuted, setShuffle, cycleRepeat, updateTrack,
    playback, setPlayback, crossfading, eq, setEq,
    radio, stopRadio: () => setRadio(null), startRadio, playStation, nowPlaying,
    liveKey: current?.live ? current.key : null,
  };
  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}

export const usePlayer = () => useContext(PlayerCtx);
